import { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery, useTheme } from "@mui/material";
import { toast } from "react-toastify";
import { apiClient } from "../../../api/httpClient";
import useDuplicateEmailCheck from "../Utilities/useDuplicateEmailCheck";
import { formatPhoneNumber } from "../Utilities/formatPhoneNumber";
import { validateCorporateContactForm } from "../Utilities/validateCorporateContactForm";
import {
  validateEmailField,
  validateUsernameField,
  validatePasswordField,
  validateConfirmPasswordField,
} from "../registrationHelpers";
import {
  applyCreatedLawFirmToState,
  applyExistingLawFirmToState,
  checkFuzzyLawFirmMatch,
  createLawFirmWithLocation,
} from "../firstTimeRegisterLawFirm";
import {
  buildFirstTimeRegisterPayload,
  submitFirstTimeRegister,
} from "../firstTimeRegisterSubmit";
import { notifyExactLawFirmDuplicate } from "../../../features/bonds/shared/utils/lawFirmFuzzyMatch";

export const primaryColor = "#333333";
export const secondaryColor = "#666666";
export const COMPONENT_HEIGHT = 56;

export const textFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "0px",
    backgroundColor: "rgba(249, 249, 249, 0.8)",
    "& fieldset": { borderColor: "#E0E0E0", borderWidth: "2px" },
    "&:hover fieldset": { borderColor: primaryColor },
    "&.Mui-focused fieldset": { borderColor: primaryColor },
  },
};

const emptyForm = {
  firstName: "",
  lastName: "",
  email: "",
  username: "",
  password: "",
  confirmPassword: "",
  phoneNumber: "",
  dateOfBirth: "",
  address: "",
  position: "",
  business_name: "",
  is_corporate_pg: false,
};

const emptyLawFirm = {
  lawFirm: "",
  lawFirmLocation: "",
};

const emptyNewLawFirm = {
  name: "",
  website: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  phone: "",
};

const emptyContactForm = {
  full_name: "",
  email: "",
  phone_number: "",
  is_admin: false,
};

function useDebouncedValue(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

function isGuestUserType(userType) {
  return userType === "guest" || userType === "individual_bond_request";
}

function isCorporatePgUserType(userType, formData) {
  return userType === "corporate_pg" || (userType === "professional_guardian" && formData.is_corporate_pg);
}

function isLawFirmRegistration(userType) {
  return userType === "lawfirm_user" || userType === "law_firm_user" || userType === "law_firm";
}

function needsLawFirmSelection(userType) {
  return isLawFirmRegistration(userType) || userType === "independent_paralegal";
}

function useFirstTimeRegister() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const duplicateEmailCheck = useDuplicateEmailCheck();

  const [userType, setUserType] = useState("");
  const [registrationStep, setRegistrationStep] = useState(1);
  const [corporateContacts, setCorporateContacts] = useState([]);
  const [guestReferralSource, setGuestReferralSource] = useState("");
  const [guestReferralName, setGuestReferralName] = useState("");
  const [formData, setFormData] = useState(emptyForm);
  const [lawFirmData, setLawFirmData] = useState(emptyLawFirm);
  const [allLawFirms, setAllLawFirms] = useState([]);
  const [lawFirmLocationsByFirmId, setLawFirmLocationsByFirmId] = useState({});
  const [lawFirmSearch, setLawFirmSearch] = useState("");
  const [errors, setErrors] = useState({});
  const [loadingLawFirms, setLoadingLawFirms] = useState(false);
  const [lawFirmLocations, setLawFirmLocations] = useState([]);
  const [loadingLawFirmLocations, setLoadingLawFirmLocations] = useState(false);
  const [selectedLawFirmLocation, setSelectedLawFirmLocation] = useState(null);
  const [lawFirmLocationDialogOpen, setLawFirmLocationDialogOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contactForm, setContactForm] = useState(emptyContactForm);
  const [contactErrors, setContactErrors] = useState({});
  const [openLawFirmDialog, setOpenLawFirmDialog] = useState(false);
  const [newLawFirm, setNewLawFirm] = useState(emptyNewLawFirm);
  const [allCities, setAllCities] = useState([]);
  const [allStates, setAllStates] = useState([]);
  const [loadingCitiesStates, setLoadingCitiesStates] = useState(false);
  const [openSupport, setOpenSupport] = useState(false);
  const [openCalculator, setOpenCalculator] = useState(false);
  const [fuzzyMatchDialogOpen, setFuzzyMatchDialogOpen] = useState(false);
  const [fuzzyMatchResult, setFuzzyMatchResult] = useState(null);

  const debouncedLawFirmSearch = useDebouncedValue(lawFirmSearch, 300);

  const filteredLawFirms = useMemo(() => {
    if (!debouncedLawFirmSearch || debouncedLawFirmSearch.trim().length < 3) return [];
    const q = debouncedLawFirmSearch.toLowerCase();
    return allLawFirms
      .filter((f) => (f.label || "").toLowerCase().includes(q))
      .slice(0, 10);
  }, [allLawFirms, debouncedLawFirmSearch]);

  useEffect(() => {
    if (userType !== "lawfirm_user" && userType !== "independent_paralegal") return undefined;

    let cancelled = false;
    void (async () => {
      setLoadingLawFirms(true);
      try {
        const response = await apiClient("/lawfirms-locations", "GET");
        if (cancelled) return;
        const lawfirms = response?.lawfirms || [];
        const locationsCache = {};
        const mapped = lawfirms.map((firm) => {
          locationsCache[firm.lawfirm_id] = (firm.locations || []).map((location) => ({
            value: location.location_id,
            label: [location.name, location.city, location.state].filter(Boolean).join(" - "),
            name: location.name,
            address: location.address || "",
            city: location.city,
            state: location.state,
            zip_code: location.zip_code,
          }));
          return {
            value: firm.lawfirm_id,
            label: firm.name,
          };
        });
        setLawFirmLocationsByFirmId(locationsCache);
        setAllLawFirms(mapped);
      } catch {
        if (!cancelled) {
          setAllLawFirms([]);
          setLawFirmLocationsByFirmId({});
        }
      } finally {
        if (!cancelled) setLoadingLawFirms(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userType]);

  useEffect(() => {
    const lawFirmId = lawFirmData.lawFirm;
    if (!lawFirmId) {
      setLawFirmLocations([]);
      setSelectedLawFirmLocation(null);
      return undefined;
    }

    const locations = lawFirmLocationsByFirmId[lawFirmId] || [];
    setLawFirmLocations(locations);

    if (locations.length === 1) {
      const loc = locations[0];
      setLawFirmData((prev) => ({ ...prev, lawFirmLocation: loc.value }));
      setSelectedLawFirmLocation(loc);
    } else if (lawFirmData.lawFirmLocation) {
      const matched = locations.find((l) => l.value === lawFirmData.lawFirmLocation);
      if (matched) {
        setSelectedLawFirmLocation(matched);
      }
    } else {
      setSelectedLawFirmLocation(null);
    }

    return undefined;
  }, [lawFirmData.lawFirm, lawFirmLocationsByFirmId]);

  useEffect(() => {
    if (!openLawFirmDialog) return undefined;

    let cancelled = false;
    void (async () => {
      setLoadingCitiesStates(true);
      try {
        const [cities, states] = await Promise.all([
          apiClient("/fields/city/", "GET"),
          apiClient("/fields/states/", "GET"),
        ]);
        if (cancelled) return;
        setAllCities(Array.isArray(cities) ? cities : []);
        setAllStates(Array.isArray(states) ? states : []);
      } catch (error) {
        console.error("Error loading cities or states:", error);
      } finally {
        if (!cancelled) setLoadingCitiesStates(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [openLawFirmDialog]);

  const handleCloseSupport = useCallback(() => setOpenSupport(false), []);
  const handleCloseCalculator = useCallback(() => setOpenCalculator(false), []);

  const handleHeaderBackClick = useCallback(() => navigate("/"), [navigate]);
  const handleSignInLink = useCallback(
    (e) => {
      e?.preventDefault?.();
      navigate("/");
    },
    [navigate]
  );

  const handleNavigateGuestBond = useCallback(() => {
    try {
      localStorage.setItem(
        "guestReferralData",
        JSON.stringify({
          referral_source: guestReferralSource || "",
          referred_by: guestReferralName || "",
        })
      );
    } catch {
      // localStorage may be unavailable
    }
    navigate("/guest-request");
  }, [navigate, guestReferralSource, guestReferralName]);

  const handleGuestBackToSelection = useCallback(() => {
    setUserType("");
    setGuestReferralSource("");
    setGuestReferralName("");
  }, []);

  const handleUserTypeRadioChange = useCallback((e) => {
    const v = e.target.value;
    setUserType(v);
    setRegistrationStep(1);
    setFormData((prev) => ({
      ...prev,
      is_corporate_pg: v === "corporate_pg",
    }));
  }, []);

  const clearFieldError = useCallback((key) => {
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }, []);

  const validateFirstName = useCallback(() => {
    if (!formData.firstName?.trim()) {
      setErrors((p) => ({ ...p, firstName: "Required" }));
      return false;
    }
    clearFieldError("firstName");
    return true;
  }, [formData.firstName, clearFieldError]);

  const validateLastName = useCallback(() => {
    if (!formData.lastName?.trim()) {
      setErrors((p) => ({ ...p, lastName: "Required" }));
      return false;
    }
    clearFieldError("lastName");
    return true;
  }, [formData.lastName, clearFieldError]);

  const validateBusinessName = useCallback(() => {
    if (!formData.business_name?.trim()) {
      setErrors((p) => ({ ...p, business_name: "Required" }));
      return false;
    }
    clearFieldError("business_name");
    return true;
  }, [formData.business_name, clearFieldError]);

  const validatePhoneNumber = useCallback(() => {
    if (!formData.phoneNumber?.trim()) {
      setErrors((p) => ({ ...p, phoneNumber: "Required" }));
      return false;
    }
    clearFieldError("phoneNumber");
    return true;
  }, [formData.phoneNumber, clearFieldError]);

  const validateDateOfBirth = useCallback(() => {
    if (!formData.dateOfBirth) {
      setErrors((p) => ({ ...p, dateOfBirth: "Required" }));
      return false;
    }
    clearFieldError("dateOfBirth");
    return true;
  }, [formData.dateOfBirth, clearFieldError]);

  const validateAddress = useCallback(() => {
    if (!formData.address?.trim()) {
      setErrors((p) => ({ ...p, address: "Required" }));
      return false;
    }
    clearFieldError("address");
    return true;
  }, [formData.address, clearFieldError]);

  const validateEmail = useCallback(() => {
    const result = validateEmailField(formData.email || "");
    if (!result.isValid) {
      setErrors((p) => ({ ...p, email: result.error }));
      return false;
    }
    clearFieldError("email");
    return true;
  }, [formData.email, clearFieldError]);

  const validateUsername = useCallback(() => {
    const result = validateUsernameField(formData.username || "");
    if (!result.isValid) {
      setErrors((p) => ({ ...p, username: result.error }));
      return false;
    }
    clearFieldError("username");
    return true;
  }, [formData.username, clearFieldError]);

  const validatePassword = useCallback(() => {
    const result = validatePasswordField(formData.password || "");
    if (!result.isValid) {
      setErrors((p) => ({ ...p, password: result.error }));
      return false;
    }
    clearFieldError("password");
    return true;
  }, [formData.password, clearFieldError]);

  const validateConfirmPassword = useCallback(() => {
    const result = validateConfirmPasswordField(formData.password, formData.confirmPassword || "");
    if (!result.isValid) {
      setErrors((p) => ({ ...p, confirmPassword: result.error }));
      return false;
    }
    clearFieldError("confirmPassword");
    return true;
  }, [formData.password, formData.confirmPassword, clearFieldError]);

  const handleAddressCallback = useCallback((address, city, state, zipCode) => {
    const formattedAddress = [address, city, state, zipCode].filter(Boolean).join(", ");
    setFormData((prev) => ({ ...prev, address: formattedAddress }));
    clearFieldError("address");
  }, [clearFieldError]);

  const handleAccountEmailBlur = useCallback(() => {
    const ok = validateEmail();
    const em = formData.email?.trim();
    if (ok && em?.includes("@")) duplicateEmailCheck.checkEmail(em);
  }, [validateEmail, formData.email, duplicateEmailCheck]);

  const handleToggleShowPassword = useCallback(() => setShowPassword((s) => !s), []);
  const handleToggleShowConfirmPassword = useCallback(
    () => setShowConfirmPassword((s) => !s),
    []
  );

  const handleOpenLawFirmDialog = useCallback(() => setOpenLawFirmDialog(true), []);
  const handleCloseLawFirmDialog = useCallback(() => {
    setOpenLawFirmDialog(false);
    setNewLawFirm(emptyNewLawFirm);
  }, []);

  const handleLawFirmAddressCallback = useCallback((address, city, state, zipCode) => {
    if (address == null && city == null && state == null && zipCode == null) return;
    setNewLawFirm((prev) => ({
      ...prev,
      address: address ?? prev.address,
      city: city ?? prev.city,
      state: state ?? prev.state,
      zip: zipCode ?? prev.zip,
    }));
  }, []);

  const handleNewLawFirmPhoneChange = useCallback((e) => {
    setNewLawFirm((prev) => ({ ...prev, phone: e.target.value }));
  }, []);

  const handleLawFirmAutocompleteChange = useCallback((_, option) => {
    if (!option) {
      setLawFirmData(emptyLawFirm);
      setSelectedLawFirmLocation(null);
      setLawFirmLocations([]);
      return;
    }
    setLawFirmData((prev) => ({ ...prev, lawFirm: option.value, lawFirmLocation: "" }));
    setSelectedLawFirmLocation(null);
  }, []);

  const handleLawFirmSearchInputChange = useCallback((_, value) => {
    setLawFirmSearch(value ?? "");
  }, []);

  const handleSelectLawFirmLocationFromTable = useCallback((loc) => {
    setLawFirmData((prev) => ({ ...prev, lawFirmLocation: loc.value }));
    setSelectedLawFirmLocation(loc);
    setLawFirmLocationDialogOpen(false);
  }, []);

  const handlePositionAutocompleteChange = useCallback((_, value) => {
    setFormData((prev) => ({ ...prev, position: value || "" }));
    clearFieldError("position");
  }, [clearFieldError]);

  const handleRemoveCorporateContact = useCallback((idx) => {
    setCorporateContacts((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleAddCorporateContact = useCallback(() => {
    const validationErrors = validateCorporateContactForm(contactForm);
    if (Object.keys(validationErrors).length > 0) {
      setContactErrors(validationErrors);
      return;
    }
    setContactErrors({});
    setCorporateContacts((prev) => [...prev, { ...contactForm }]);
    setContactForm(emptyContactForm);
  }, [contactForm]);

  const handleFormSecondaryBack = useCallback(() => {
    if (userType) {
      setUserType("");
      setFormData(emptyForm);
      setLawFirmData(emptyLawFirm);
      setRegistrationStep(1);
      setErrors({});
    } else {
      navigate("/");
    }
  }, [userType, navigate]);

  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      if (name === "email") duplicateEmailCheck.reset();

      if (name === "phoneNumber") {
        setFormData((prev) => ({ ...prev, phoneNumber: formatPhoneNumber(value) }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }

      if (name === "password") {
        setErrors((prev) => {
          if (!prev.password && !prev.confirmPassword) return prev;
          return {
            ...prev,
            password: undefined,
            confirmPassword: undefined,
          };
        });
        return;
      }

      setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
    },
    [duplicateEmailCheck]
  );

  const handleAddLawFirm = useCallback(async () => {
    try {
      setLoadingLawFirms(true);
      const fuzzyResult = await checkFuzzyLawFirmMatch(newLawFirm);
      if (fuzzyResult.hasMatch) {
        setFuzzyMatchResult({
          id: fuzzyResult.matchedId,
          name: fuzzyResult.matchedName,
          exactMatch: fuzzyResult.exactMatch,
        });
        setFuzzyMatchDialogOpen(true);
        return;
      }

      const { lawFirmResponse, locationResponse } = await createLawFirmWithLocation(newLawFirm);
      applyCreatedLawFirmToState({
        newLawFirm,
        lawFirmResponse,
        locationResponse,
        setAllLawFirms,
        setLawFirmLocationsByFirmId,
        setLawFirmData,
        setLawFirmLocations,
        setSelectedLawFirmLocation,
        setLawFirmSearch,
        setErrors,
      });
      toast.success("Law Firm and location added successfully!");
      handleCloseLawFirmDialog();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  }, [newLawFirm, handleCloseLawFirmDialog]);

  const handleUseExistingLawFirm = useCallback(() => {
    if (!fuzzyMatchResult) return;
    applyExistingLawFirmToState({
      fuzzyMatchResult,
      setAllLawFirms,
      setLawFirmData,
      setLawFirmSearch,
      setErrors,
    });
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    handleCloseLawFirmDialog();
    toast.success("Using existing law firm.");
  }, [fuzzyMatchResult, handleCloseLawFirmDialog]);

  const handleCreateNewLawFirmAnyway = useCallback(async () => {
    if (notifyExactLawFirmDuplicate(fuzzyMatchResult?.exactMatch, toast)) {
      return;
    }
    setFuzzyMatchDialogOpen(false);
    setFuzzyMatchResult(null);
    try {
      setLoadingLawFirms(true);
      const { lawFirmResponse, locationResponse } = await createLawFirmWithLocation(newLawFirm);
      applyCreatedLawFirmToState({
        newLawFirm,
        lawFirmResponse,
        locationResponse,
        setAllLawFirms,
        setLawFirmLocationsByFirmId,
        setLawFirmData,
        setLawFirmLocations,
        setSelectedLawFirmLocation,
        setLawFirmSearch,
        setErrors,
      });
      toast.success("Law Firm and location added successfully!");
      handleCloseLawFirmDialog();
    } catch (error) {
      console.error("Error adding law firm:", error);
      toast.error("Failed to add law firm!");
    } finally {
      setLoadingLawFirms(false);
    }
  }, [newLawFirm, fuzzyMatchResult, handleCloseLawFirmDialog]);

  const validateForm = useCallback(() => {
    if (!userType) return false;

    if (isGuestUserType(userType)) {
      return !!(
        String(guestReferralSource || "").trim() && String(guestReferralName || "").trim()
      );
    }

    let isValid = true;

    if (!validateEmail()) isValid = false;
    if (!validateUsername()) isValid = false;
    if (!validatePassword()) isValid = false;
    if (!validateConfirmPassword()) isValid = false;
    if (!validateFirstName()) isValid = false;
    if (!validateLastName()) isValid = false;

    const pgContactTypes = [
      "professional_guardian",
      "individual_pg",
      "corporate_pg",
      "independent_paralegal",
    ];
    if (pgContactTypes.includes(userType)) {
      if (!validatePhoneNumber()) isValid = false;
      if (!validateAddress()) isValid = false;
      if (!isCorporatePgUserType(userType, formData) && !validateDateOfBirth()) isValid = false;
      if (isCorporatePgUserType(userType, formData) && !validateBusinessName()) isValid = false;
    }

    if (needsLawFirmSelection(userType)) {
      if (!lawFirmData.lawFirm) {
        setErrors((prev) => ({ ...prev, lawFirm: "Please select a law firm" }));
        isValid = false;
      }
      if (lawFirmData.lawFirm && lawFirmLocations.length > 0 && !lawFirmData.lawFirmLocation) {
        setErrors((prev) => ({ ...prev, lawFirmLocation: "Please select a law firm location" }));
        isValid = false;
      }
      if (
        (userType === "lawfirm_user" || userType === "law_firm_user") &&
        !formData.position?.trim()
      ) {
        setErrors((prev) => ({ ...prev, position: "Required" }));
        isValid = false;
      }
    }

    return isValid;
  }, [
    userType,
    formData,
    guestReferralSource,
    guestReferralName,
    lawFirmData,
    lawFirmLocations,
    validateEmail,
    validateUsername,
    validatePassword,
    validateConfirmPassword,
    validateFirstName,
    validateLastName,
    validatePhoneNumber,
    validateAddress,
    validateDateOfBirth,
    validateBusinessName,
  ]);

  const handleRegister = useCallback(async () => {
    if (registrationStep === 1 && userType === "professional_guardian" && formData.is_corporate_pg) {
      setRegistrationStep(2);
      return;
    }
    if (registrationStep === 1 && userType === "corporate_pg") {
      if (!validateForm()) {
        toast.error("Please fill in all required fields correctly.");
        return;
      }
      setRegistrationStep(2);
      return;
    }
    if (!validateForm()) {
      toast.error("Please fill in all required fields correctly.");
      return;
    }

    if (duplicateEmailCheck.hasDuplicate) {
      toast.error("This email is already registered. Please sign in or recover your username.");
      return;
    }

    if (isGuestUserType(userType)) {
      return;
    }

    setLoading(true);
    try {
      const payload = buildFirstTimeRegisterPayload({
        userType,
        formData,
        lawFirmData,
        corporateContacts,
      });
      await submitFirstTimeRegister(payload, { navigate, toast });
    } catch {
      // Errors surfaced via toast in submitFirstTimeRegister
    } finally {
      setLoading(false);
    }
  }, [
    registrationStep,
    userType,
    formData,
    lawFirmData,
    corporateContacts,
    validateForm,
    duplicateEmailCheck.hasDuplicate,
    navigate,
  ]);

  const handleFormKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter") void handleRegister();
    },
    [handleRegister]
  );

  const firstTimeSubmitDisabled =
    loading || duplicateEmailCheck.checking || duplicateEmailCheck.hasDuplicate;

  return {
    isMobile,
    openSupport,
    setOpenSupport,
    handleCloseSupport,
    openCalculator,
    setOpenCalculator,
    handleCloseCalculator,
    userType,
    setUserType,
    registrationStep,
    setRegistrationStep,
    corporateContacts,
    setCorporateContacts,
    guestReferralSource,
    setGuestReferralSource,
    guestReferralName,
    setGuestReferralName,
    formData,
    lawFirmData,
    setLawFirmData,
    allLawFirms,
    setAllLawFirms,
    filteredLawFirms,
    errors,
    setErrors,
    lawFirmSearch,
    setLawFirmSearch,
    loadingLawFirms,
    setLoadingLawFirms,
    handleOpenLawFirmDialog,
    lawFirmLocations,
    setLawFirmLocations,
    loadingLawFirmLocations,
    setLoadingLawFirmLocations,
    selectedLawFirmLocation,
    setSelectedLawFirmLocation,
    lawFirmLocationDialogOpen,
    setLawFirmLocationDialogOpen,
    handleInputChange,
    validateFirstName,
    validateLastName,
    validateBusinessName,
    validatePhoneNumber,
    validateDateOfBirth,
    handleAddressCallback,
    validateAddress,
    validateEmail,
    handleAccountEmailBlur,
    validateUsername,
    validatePassword,
    validateConfirmPassword,
    showPassword,
    showConfirmPassword,
    handleRegister,
    handleFormKeyDown,
    firstTimeSubmitDisabled,
    loading,
    contactForm,
    setContactForm,
    contactErrors,
    setContactErrors,
    formatPhoneNumber,
    handleHeaderBackClick,
    handleUserTypeRadioChange,
    handleNavigateGuestBond,
    handleGuestBackToSelection,
    handleLawFirmAutocompleteChange,
    handleLawFirmSearchInputChange,
    handleSelectLawFirmLocationFromTable,
    handlePositionAutocompleteChange,
    handleToggleShowPassword,
    handleToggleShowConfirmPassword,
    handleRemoveCorporateContact,
    handleAddCorporateContact,
    handleFormSecondaryBack,
    handleSignInLink,
    openLawFirmDialog,
    handleCloseLawFirmDialog,
    newLawFirm,
    setNewLawFirm,
    handleLawFirmAddressCallback,
    allCities,
    setAllCities,
    allStates,
    setAllStates,
    loadingCitiesStates,
    setLoadingCitiesStates,
    handleAddLawFirm,
    handleNewLawFirmPhoneChange,
    fuzzyMatchDialogOpen,
    setFuzzyMatchDialogOpen,
    fuzzyMatchResult,
    setFuzzyMatchResult,
    handleUseExistingLawFirm,
    handleCreateNewLawFirmAnyway,
    duplicateEmailCheck,
  };
}

export default useFirstTimeRegister;
